// Toolbar edits for a Markdown textarea (bold, italic, H2, bullet and numbered lists, link) as pure
// functions on the text and the selection, so the editors share one behaviour (SAM-305 event editor,
// SAM-1073 new service description).
export type MarkdownKind = 'bold' | 'italic' | 'heading' | 'heading3' | 'bullet' | 'numbered' | 'link';

export interface MarkdownEdit {
  value: string;
  selStart: number;
  selEnd: number;
}

/** `url` is only read for 'link'; an empty url cancels it (null). */
export function applyMarkdownEdit(
  value: string,
  start: number,
  end: number,
  kind: MarkdownKind,
  url?: string,
): MarkdownEdit | null {
  const selected = value.slice(start, end);
  if (kind === 'bold' || kind === 'italic') {
    const marker = kind === 'bold' ? '**' : '*';
    const text = selected || (kind === 'bold' ? 'bold text' : 'italic text');
    const selStart = start + marker.length;
    return {
      value: value.slice(0, start) + `${marker}${text}${marker}` + value.slice(end),
      selStart,
      selEnd: selStart + text.length,
    };
  }
  if (kind === 'link') {
    if (!url) return null;
    const text = selected || 'link text';
    const selStart = start + 1;
    return {
      value: value.slice(0, start) + `[${text}](${url})` + value.slice(end),
      selStart,
      selEnd: selStart + text.length,
    };
  }
  const lineStart = value.lastIndexOf('\n', start - 1) + 1;
  let lineEnd = value.indexOf('\n', end);
  if (lineEnd === -1) lineEnd = value.length;
  let n = 1;
  const prefixed = value.slice(lineStart, lineEnd).split('\n')
    .map((line) => {
      if (line.trim() === '') return line;
      if (kind === 'heading' || kind === 'heading3') {
        const mark = kind === 'heading' ? '##' : '###';
        const bare = line.replace(/^\s{0,3}#{1,3}\s+/, '');
        // The same heading again takes it off; another level replaces it.
        return line.startsWith(`${mark} `) ? bare : `${mark} ${bare}`;
      }
      return kind === 'bullet' ? `- ${line}` : `${n++}. ${line}`;
    })
    .join('\n');
  return {
    value: value.slice(0, lineStart) + prefixed + value.slice(lineEnd),
    selStart: lineStart,
    selEnd: lineStart + prefixed.length,
  };
}
